import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { User, onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, type AuthError } from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, googleProvider, appleProvider, db, isFirebaseConfigured } from '../config/firebase'
import toast from 'react-hot-toast'

interface UserProfile {
  uid: string
  email: string
  displayName: string
  photoURL?: string
  subscriptionStatus: 'free' | 'pro' | 'enterprise'
  tokensUsed: number
  tokensLimit: number
  createdAt: Date
  lastLoginAt: Date
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  // Handle redirect result on page load (fallback from popup-blocked)
  useEffect(() => {
    if (!auth) return
    getRedirectResult(auth).catch(() => {
      // Redirect result errors are non-fatal (no redirect in progress = normal)
    })
  }, [])

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user)

      if (user && db) {
        // Get or create user profile
        const userDoc = await getDoc(doc(db, 'users', user.uid))

        if (userDoc.exists()) {
          setUserProfile(userDoc.data() as UserProfile)
          // Update last login
          await setDoc(doc(db, 'users', user.uid), {
            lastLoginAt: new Date()
          }, { merge: true })
        } else {
          // Create new user profile
          const newProfile: UserProfile = {
            uid: user.uid,
            email: user.email!,
            displayName: user.displayName!,
            photoURL: user.photoURL || undefined,
            subscriptionStatus: 'free',
            tokensUsed: 0,
            tokensLimit: 1000, // Free tier limit
            createdAt: new Date(),
            lastLoginAt: new Date()
          }

          try {
            await setDoc(doc(db, 'users', user.uid), newProfile)
            setUserProfile(newProfile)

            // The welcome email is sent by the onUserCreate trigger, which fires on
            // the write above. There is deliberately no client write to mailingList:
            // its rules are `allow read, write: if false` (functions only) and the
            // only function that touches it is an onCreate trigger that logs, so the
            // write could never succeed. It threw on every signup and surfaced as
            // "we hit a snag saving your profile" — while the profile had in fact
            // saved fine.
            toast.success('Welcome to JustFixIt!')
          } catch (error) {
            console.error('Failed to create user profile:', error)
            Sentry.captureException(error, { tags: { flow: 'user_profile_create' } })
            setUserProfile(newProfile) // Keep the session usable locally
            toast.error('Account created, but we hit a snag saving your profile.')
          }
        }
      } else {
        setUserProfile(null)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const signInWithGoogle = async () => {
    if (!auth || !googleProvider) {
      toast.error('Authentication is not configured.')
      return
    }
    try {
      setLoading(true)
      await signInWithPopup(auth, googleProvider)
      toast.success('Successfully signed in!')
    } catch (error) {
      const authError = error as AuthError
      if (authError.code === 'auth/popup-blocked') {
        // Popup blocked by browser — fall back to redirect flow
        toast('Redirecting to Google sign-in...', { icon: '🔄' })
        // Clear stale PKCE state from failed popup attempt
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('firebase:')) {
            sessionStorage.removeItem(key)
          }
        }
        await signInWithRedirect(auth, googleProvider)
        return
      }
      if (authError.code === 'auth/popup-closed-by-user') {
        // User closed the popup — not an error worth reporting
        return
      }
      console.error('Sign in error:', error)
      toast.error('Failed to sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const signUpWithEmail = async (email: string, password: string) => {
    if (!auth) {
      toast.error('Authentication is not configured.')
      return
    }
    try {
      setLoading(true)
      await createUserWithEmailAndPassword(auth, email, password)
      toast.success('Account created! Welcome to JustFixIt!')
    } catch (error) {
      const authError = error as AuthError
      if (authError.code === 'auth/email-already-in-use') {
        toast.error('That email is already registered. Try signing in instead.')
      } else if (authError.code === 'auth/weak-password') {
        toast.error('Password must be at least 6 characters.')
      } else if (authError.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.')
      } else {
        console.error('Sign up error:', error)
        toast.error('Failed to create account. Please try again.')
      }
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signInWithEmail = async (email: string, password: string) => {
    if (!auth) {
      toast.error('Authentication is not configured.')
      return
    }
    try {
      setLoading(true)
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('Successfully signed in!')
    } catch (error) {
      const authError = error as AuthError
      if (authError.code === 'auth/user-not-found' || authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password.')
      } else if (authError.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again later.')
      } else {
        console.error('Sign in error:', error)
        toast.error('Failed to sign in. Please try again.')
      }
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signInWithApple = async () => {
    if (!auth || !appleProvider) {
      toast.error('Authentication is not configured.')
      return
    }
    try {
      setLoading(true)
      await signInWithPopup(auth, appleProvider)
      toast.success('Successfully signed in!')
    } catch (error) {
      const authError = error as AuthError
      if (authError.code === 'auth/popup-blocked') {
        toast('Redirecting to Apple sign-in...', { icon: '🔄' })
        // Clear stale PKCE state from failed popup attempt
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('firebase:')) {
            sessionStorage.removeItem(key)
          }
        }
        await signInWithRedirect(auth, appleProvider)
        return
      }
      if (authError.code === 'auth/popup-closed-by-user') {
        return
      }
      console.error('Apple sign in error:', error)
      toast.error('Failed to sign in with Apple. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string) => {
    if (!auth) {
      toast.error('Authentication is not configured.')
      return
    }
    try {
      await sendPasswordResetEmail(auth, email)
      toast.success('Password reset email sent! Check your inbox.')
    } catch (error) {
      const authError = error as AuthError
      if (authError.code === 'auth/user-not-found') {
        toast.error('No account found with that email.')
      } else if (authError.code === 'auth/invalid-email') {
        toast.error('Please enter a valid email address.')
      } else {
        console.error('Password reset error:', error)
        toast.error('Failed to send reset email. Please try again.')
      }
      throw error
    }
  }

  const logout = async () => {
    if (!auth) return
    try {
      await signOut(auth)
      toast.success('Successfully signed out!')
    } catch (error) {
      console.error('Sign out error:', error)
      toast.error('Failed to sign out.')
    }
  }

  return {
    user,
    userProfile,
    loading,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    logout
  }
}